import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from '../login/LoginForm';
import { transcriptApi } from '../../api/transcriptApi';
import { reportApi } from '../../api/reportApi';
import ReportEditor from '../../components/ReportEditor';
import Notification from '../../components/ui/Notification';

/**
 * Report Editor Page
 * 
 * Advanced report editing interface for sessions
 */
const ReportEditorPage = () => {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, error: authError, getAuth } = useAuth();

  const [sessionData, setSessionData] = useState(null);
  const [reportData, setReportData] = useState({
    session_at_glance_md: '',
    full_report_md: '',
    speakers_md: '',
    in_depth_md: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notif, setNotif] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savingTab, setSavingTab] = useState(null);
  const [saveErrors, setSaveErrors] = useState({});

  /**
   * Fetch session and report data
   */
  useEffect(() => {
    if (!user || !sessionId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch session data
        const sessionRes = await transcriptApi.getSessionById(sessionId);
        setSessionData(sessionRes);

        // First, try to get markdown fields from session object (if manually edited)
        let normalized = {
          session_at_glance_md: sessionRes.session_at_glance_md || sessionRes.sessionAtGlanceMd || '',
          full_report_md: sessionRes.full_report_md || sessionRes.fullReportMd || '',
          speakers_md: sessionRes.speakers_md || sessionRes.speakersMd || '',
          in_depth_md: sessionRes.in_depth_md || sessionRes.inDepthMd || '',
        };

        // Always try to load from report data to fill in missing fields
        try {
          // Try to load report status and report data
          const reportStatus = await reportApi.getSessionReportStatus(sessionId);
          
          if (reportStatus.status === 'COMPLETED') {
            const report = await reportApi.getSessionReport(sessionId);
              
              // Parse markdown fields from report data
              if (report.data && report.data['Session report']) {
                const sessionReport = report.data['Session report'];
                
                // Populate "Session at a glance" (only if not already set)
                if (!normalized.session_at_glance_md && sessionReport['Session at a glance']) {
                  const atGlance = sessionReport['Session at a glance'];
                  let atGlanceMarkdown = '';
                  
                  if (atGlance.Summary) {
                    atGlanceMarkdown += `## Summary\n\n${atGlance.Summary}\n\n`;
                  }
                  if (atGlance.Keypoints) {
                    atGlanceMarkdown += `## Keypoints\n\n${atGlance.Keypoints}\n\n`;
                  }
                  if (atGlance.Speakers) {
                    atGlanceMarkdown += `## Speakers\n\n${atGlance.Speakers}\n\n`;
                  }
                  
                  if (atGlanceMarkdown) {
                    normalized.session_at_glance_md = atGlanceMarkdown.trim();
                  }
                }
                
                // Populate "Full session report" (only if not already set)
                if (!normalized.full_report_md && sessionReport['Full session report']) {
                  normalized.full_report_md = sessionReport['Full session report'];
                }
              }
              
              // Populate Speakers from report data (only if not already set)
              if (!normalized.speakers_md && report.data && report.data.Speakers) {
                const speakers = report.data.Speakers;
                let speakersMarkdown = '';
                
                Object.keys(speakers).forEach(speakerName => {
                  const speakerData = speakers[speakerName];
                  speakersMarkdown += `## ${speakerName}\n\n`;
                  
                  Object.keys(speakerData).forEach(argumentName => {
                    const argumentData = speakerData[argumentName];
                    speakersMarkdown += `### ${argumentName}\n\n`;
                    
                    if (argumentData.Explanation) {
                      speakersMarkdown += `**Explanation:** ${argumentData.Explanation}\n\n`;
                    }
                    if (argumentData.Evidence) {
                      speakersMarkdown += `**Evidence:** ${argumentData.Evidence}\n\n`;
                    }
                    if (argumentData['Major discussion point']) {
                      speakersMarkdown += `**Major discussion point:** ${argumentData['Major discussion point']}\n\n`;
                    }
                    speakersMarkdown += '\n';
                  });
                });
                
                if (speakersMarkdown) {
                  normalized.speakers_md = speakersMarkdown.trim();
                }
              }
              
              // Populate In-depth analysis from report data (only if not already set)
              if (!normalized.in_depth_md && report.data && report.data['Additional analysis']) {
                const additionalAnalysis = report.data['Additional analysis'];
                let inDepthMarkdown = '';
                
                if (additionalAnalysis.Agreements) {
                  inDepthMarkdown += `## Agreements\n\n`;
                  // Add agreements content
                }
                if (additionalAnalysis.Disagreements) {
                  inDepthMarkdown += `## Disagreements\n\n`;
                  // Add disagreements content
                }
                if (additionalAnalysis['Thought provoking comments']) {
                  inDepthMarkdown += `## Thought Provoking Comments\n\n`;
                  // Add comments content
                }
                if (additionalAnalysis['Follow-up questions']) {
                  inDepthMarkdown += `## Follow-up Questions\n\n`;
                  // Add questions content
                }
                
                if (inDepthMarkdown) {
                  normalized.in_depth_md = inDepthMarkdown.trim();
                }
              }
            }
          } catch (reportErr) {
            console.warn('Could not load report data, using session data only:', reportErr);
            // Continue with session data only
          }

        setReportData(normalized);
      } catch (err) {
        console.error('Error fetching session data:', err);
        setError(err.message || 'Failed to load session');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, sessionId]);

  /**
   * Handle save operation
   */
  const handleSave = async (payload) => {
    const extractErrorMessage = (err) => {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string' && detail.trim()) return detail;
      if (Array.isArray(detail)) {
        // FastAPI validation errors: [{loc: [...], msg: "...", type: "..."}]
        const msgs = detail
          .map((e) => {
            const loc = Array.isArray(e?.loc) ? e.loc.join('.') : '';
            const msg = e?.msg || e?.message || '';
            return [loc, msg].filter(Boolean).join(': ');
          })
          .filter(Boolean);
        if (msgs.length) return msgs.join(' | ');
      }
      if (typeof err?.response?.data === 'string' && err.response.data.trim()) return err.response.data;
      if (err?.message) return err.message;
      try {
        return JSON.stringify(err?.response?.data || err);
      } catch {
        return 'Failed to save changes';
      }
    };

    try {
      setIsSaving(true);
      setSaveErrors({});

      // Determine which tabs are being saved
      const tabKeys = {
        session_at_glance_md: 'at_glance',
        full_report_md: 'full_report',
        speakers_md: 'speakers',
        in_depth_md: 'analysis',
      };

      const payloadKeys = Object.keys(payload);
      const isSavingAll = payloadKeys.length > 1;
      
      if (!isSavingAll) {
        const tabId = tabKeys[payloadKeys[0]];
        setSavingTab(tabId);
      } else {
        setSavingTab('all');
      }

      // Save to API
      await transcriptApi.updateSession(sessionId, payload);

      // Update local state
      setReportData(prev => ({ ...prev, ...payload }));

      // Show success notification
      if (isSavingAll) {
        setNotif({
          type: 'success',
          title: 'Saved',
          message: 'All report sections saved successfully.',
        });
      } else {
        const tabNames = {
          at_glance: 'Session at a glance',
          full_report: 'Full report',
          speakers: 'Speakers',
          analysis: 'In-depth analysis',
        };
        const tabId = tabKeys[payloadKeys[0]];
        setNotif({
          type: 'success',
          title: 'Saved',
          message: `${tabNames[tabId] || 'Report'} saved successfully.`,
        });
      }

      return true;
    } catch (err) {
      console.error('Save failed:', err);
      const errMsg = extractErrorMessage(err);
      const tabKeys = {
        session_at_glance_md: 'at_glance',
        full_report_md: 'full_report',
        speakers_md: 'speakers',
        in_depth_md: 'analysis',
      };
      
      const payloadKeys = Object.keys(payload);
      if (payloadKeys.length === 1) {
        const tabId = tabKeys[payloadKeys[0]];
        setSaveErrors({
          [tabId]: errMsg,
        });
      } else {
        // Multiple tabs - set error for all
        const errors = {};
        payloadKeys.forEach(key => {
          const tabId = tabKeys[key];
          if (tabId) {
            errors[tabId] = errMsg;
          }
        });
        setSaveErrors(errors);
      }
      
      setNotif({
        type: 'error',
        title: 'Save failed',
        message: errMsg,
      });

      throw err;
    } finally {
      setIsSaving(false);
      setSavingTab(null);
    }
  };

  /**
   * Handle cancel/back navigation
   */
  const handleCancel = () => {
    navigate(`/sessions/${sessionId}`);
  };

  // Show login form if not authenticated
  if (!user) {
    return (
      <LoginForm
        onSSOLogin={getAuth}
        ssoLoading={authLoading}
        ssoError={authError}
      />
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading report editor...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/sessions')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Back to Sessions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Notification
        type={notif?.type}
        title={notif?.title}
        message={notif?.message}
        onClose={() => setNotif(null)}
      />
      
      <ReportEditor
        sessionId={sessionId}
        sessionTitle={sessionData?.title || 'Report Editor'}
        initialData={reportData}
        onSave={handleSave}
        onCancel={handleCancel}
        isSaving={isSaving}
        saveErrors={saveErrors}
      />
    </div>
  );
};

export default ReportEditorPage;

